import { routerNavigatedAction } from '@ngrx/router-store';
import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { current, produce } from 'immer';

import {
  type EngineStateBase,
  getNavMenuInitialState,
  runtimeActions,
  type RuntimeState,
} from '@blaze-runtime/engine-data-access';
import { getNamespaceId } from '@blaze-runtime/engine-domain';
import {
  type ConnectionChain,
  getChainByChainId,
  getChainIndexByChainId,
} from '@blaze-runtime/engine-domain';
import { type PageInfo } from '@blaze-shared/api-contracts-domain';
import { appStateActions, blazeActions } from '@blaze-shared/app-state-data-access';
import {
  BULK_ACTION_IDS_PROP,
  type ElementMetaData,
  type ElementOutputUi,
  type ElementQueryState,
  type FormState,
} from '@blaze-shared/elements-domain';
import { compareAscBy } from '@blaze-shared/platform-util';
import { blazePageActions } from '@blaze-shared/projects-data-access';
import { schemaActions } from '@blaze-shared/schema-data-access';
import {
  convertWorkflowSchemaToElementSchema,
  createElementSchemaId,
  isActionEventType,
  isDatasetLinkValue,
  isElementSchema,
  isWorkflowSchema,
} from '@blaze-shared/schema-domain';
import { deepClone, deepmergeExceptArrays, isNullish } from '@blaze-shared/shared-util';
import { isActionWorkflow, WorkflowPhase } from '@blaze-shared/workflow-domain';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import {
  isDataFilter,
  isEditableForm,
  isVariantOfFormContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { DEFAULT_PAGE_SIZE } from '../../services/element-pagination.service';
import { getGlobalQueryForElement } from '../../utils/data/global-query.utils';
import { isSelfBindingForm } from '../../utils/elements/form.utils';
import { upsertConnections } from '../../utils/runtime/connection';
import * as elementsActions from '../elements/elements.actions';
import { entityAdapter } from '../engine/engine-adapters';
import { resetEngineState, stopRuntimeAndResetElementsState } from '../engine/engine.actions';
import { runtimeError } from '../runtime-errors/runtime-errors.actions';

import {
  addConnectionMutably,
  addOrUpdateConnectionMutably,
  addOrUpdateWorkflowMutably,
  applyAiGeneratedWorkflowMutably,
  clearElementOutputsMutably,
  dropStaleSlotSelectionMutably,
  getDataFilterWorkflowsInputNames,
  removePendingUiMutably,
  replaceConnectionsForNsMutably,
  sanitizeConnectionsAndDataAfterUiChangeMutably,
  stopRuntime,
  updateQueryStateMutably,
  updateRuntimePagesMutably,
  updateWorkflowMutably,
  updateWrapperConnectionsMutably,
} from './runtime.reducer.utils';

const sortByNameWithHomepageOnTop = (a: PageInfo, b: PageInfo): number => {
  if (a.isHomePage) {
    return -1;
  }
  if (b.isHomePage) {
    return 1;
  }
  return compareAscBy('name')(a, b);
};

export const initialRuntimeState: RuntimeState = {
  bearerToken: undefined,
  connectionChains: {},
  connections: [],
  elementsMeta: {},
  elementsQuery: {},
  eventsUi: {},
  forms: {},
  globalQuery: {},
  initializedUiElements: {},
  internalAccount: undefined,
  internalUser: undefined,
  isCustomerPortal: undefined,
  isRunning: false,
  isRuntimeInfoFetched: false,
  missingDatasets: [],
  namespace: '',
  navMenu: getNavMenuInitialState(),
  outputsUi: {},
  pageId: '',
  pages: [],
  pendingPageCreations: {},
  pendingPaginations: {},
  pendingUi: {},
  projectId: '',
  projectName: undefined,
  publishedUrl: '',
  runtimeErrors: {},
  runtimeMode: undefined,
  runtimePages: {},
  schemas: {},
  workflowOutputs: {},
  workflows: [],
  workflowsPagination: {},
  workflowsStatus: {},
};

export const runtimeReducerTypes: ReducerTypes<EngineStateBase, ActionCreator[]>[] = [
  /*** Engine Actions */

  on(resetEngineState, (state) => {
    let resetState = entityAdapter.removeAll(state);
    resetState = {
      ...resetState,
      ...initialRuntimeState,
      layoutEntities: {},
      layoutIds: [],
      offSyncLayouts: {},
      overlays: [],
      selectedPathNode: undefined,
    };
    return resetState;
  }),

  on(stopRuntimeAndResetElementsState, (state) => {
    let newState = entityAdapter.removeAll(state);
    newState = {
      ...newState,
      connectionChains: {},
      globalQuery: {},
      isRunning: false,
      layoutEntities: {},
      layoutIds: [],
      navMenu: getNavMenuInitialState(),
      offSyncLayouts: {},
      overlays: [],
      pendingUi: {},
      publishedUrl: '',
      selectedPathNode: undefined,
      workflowsStatus: {},
    };
    return newState;
  }),

  /*** Schema Actions ***/
  on(schemaActions.addMissingDatasets, (state, { missingDatasetIds }) => {
    const missing = new Set([...(state.missingDatasets || []), ...missingDatasetIds]);
    return {
      ...state,
      missingDatasets: Array.from(missing),
    };
  }),

  on(schemaActions.addSchema, (state, { schema }) => {
    return {
      ...state,
      schemas: {
        ...state.schemas,
        [schema.id]: schema,
      },
    };
  }),

  on(schemaActions.addSchemas, (state, { schemas }) => {
    return {
      ...state,
      // Merge schemas with existing ones
      schemas: {
        ...state.schemas,
        ...schemas.reduce((acc, schema) => ({ ...acc, [schema.id]: schema }), {}),
      },
    };
  }),

  // Loading a new project should clear the state. Do not set projectId passed via the action in here. projectId
  // is only used by the effects in other modules.
  on(appStateActions.loadProjectRuntime, (state, { pageId, projectName, resetConnections }) => {
    const newRuntimeState: RuntimeState = {
      ...initialRuntimeState,
      projectName,
      runtimeMode: state.runtimeMode,
      runtimePages: state.runtimePages,
    };
    if (pageId) {
      newRuntimeState.pageId = pageId;
    }

    if (!resetConnections) {
      newRuntimeState.connections = state.connections;
    }

    return {
      ...state,
      ...newRuntimeState,
    };
  }),

  on(blazeActions.navigateToPage, (state, { projectId, pageId }) => {
    const updatedState = { ...state };
    updatedState.projectId = projectId;

    // Clear connection chains from the previously loaded page.
    if (state.pageId !== pageId) {
      updatedState.connectionChains = {
        ...updatedState.connectionChains,
        [state.pageId]: [],
      };
      updatedState.globalQuery = {};
    }

    updatedState.pageId = pageId;

    return updatedState;
  }),

  on(blazeActions.setPublishedUrl, (state, { url, bearerToken }) =>
    produce(state, (draft) => {
      draft.publishedUrl = url;
      draft.bearerToken = bearerToken;
    }),
  ),

  on(appStateActions.setRuntimeMode, (state, { runtimeMode }) =>
    produce(state, (draft) => {
      draft.runtimeMode = runtimeMode;
    }),
  ),

  on(appStateActions.setRuntimeSettingsFromProjectInfo, (state, { isCustomerPortal }) =>
    produce(state, (draft) => {
      draft.isCustomerPortal = isCustomerPortal;
    }),
  ),

  on(appStateActions.updateProjectSettings, (state, { settings }) =>
    produce(state, (draft) => {
      if ('isCustomerPortal' in settings) {
        draft.isCustomerPortal = settings.isCustomerPortal;
      }
    }),
  ),

  on(blazeActions.startRuntime, (state, { projectId, pageId }) =>
    produce(state, (draft) => {
      draft.projectId = projectId;
      draft.namespace = pageId;
      draft.pageId = pageId;
      draft.isRunning = true;
    }),
  ),

  on(routerNavigatedAction, (state, { payload }) => {
    // If navigating to the project list, we stop the runtime and clear schemas.
    if (payload?.routerState?.url === '/projects') {
      const updatedState = {
        ...stopRuntime(state),
        schemas: {},
      };

      return updatedState;
    }

    return state;
  }),

  on(blazeActions.stopRuntime, (state) => {
    return stopRuntime(state);
  }),

  on(blazeActions.setRuntimePage, (state, { pageId, runtimePage }) =>
    produce(state, (draft) => {
      draft.pageId = pageId;
      updateRuntimePagesMutably(draft, [{ pageId, info: runtimePage }]);
    }),
  ),

  on(blazeActions.updateRuntimePages, (state, { runtimePages, pages }) =>
    produce(state, (draft) => {
      if (pages) {
        draft.pages = pages;
      }
      if (runtimePages) {
        updateRuntimePagesMutably(draft, runtimePages);
      }
    }),
  ),

  /*** Blaze Page Actions */
  on(blazePageActions.addAndSetPage, (state, { page, creationData }) =>
    produce(state, (draft) => {
      draft.pages = [...draft.pages, page].toSorted(sortByNameWithHomepageOnTop);
      draft.pageId = page.id;
      if (creationData) {
        draft.pendingPageCreations[page.id] = creationData;
      }
    }),
  ),

  on(blazePageActions.addOrUpdatePage, (state, { page }) =>
    produce(state, (draft) => {
      const pageIdx = draft.pages.findIndex((p) => p.id === page.id);
      if (pageIdx >= 0) {
        draft.pages[pageIdx] = page;
      } else {
        draft.pages.push(page);
      }
      draft.pages.sort(sortByNameWithHomepageOnTop);
    }),
  ),

  on(
    blazePageActions.convertToPageSuccess,
    blazePageActions.convertToDialogSuccess,
    blazePageActions.convertToDrawerSuccess,
    (state, { updated }) =>
      produce(state, (draft) => {
        draft.pages = draft.pages.map((p) => (p.id === updated.id ? updated : p));
        updateRuntimePagesMutably(draft, [
          {
            pageId: updated.id,
            info: updated,
          },
        ]);
      }),
  ),

  on(blazePageActions.deletePageSuccess, (state, { pageId }) =>
    produce(state, (draft) => {
      if (draft.pageId === pageId) {
        draft.pageId = draft.pages[0].id;
      }
      draft.pages = draft.pages.filter((p) => p.id !== pageId);
    }),
  ),

  on(blazePageActions.duplicatePageSuccess, (state, { page }) =>
    produce(state, (draft) => {
      draft.pages.push(page);
    }),
  ),

  on(blazePageActions.pagesLoaded, blazePageActions.pagesReloaded, (state, { pages }) =>
    produce(state, (draft) => {
      draft.pages = pages;
    }),
  ),

  on(blazePageActions.setCurrentPageId, (state, { currentPageId }) =>
    produce(state, (draft) => {
      draft.pageId = currentPageId;
    }),
  ),

  on(blazePageActions.updatePageSuccess, (state, { page }) =>
    produce(state, (draft) => {
      const originalPageIdx = draft.pages.findIndex((p) => p.id === page.id);
      const originalPageValue = current(draft.pages[originalPageIdx]);
      draft.pages[originalPageIdx] = { ...originalPageValue, ...page };

      if (!originalPageValue?.isHomePage && page.isHomePage) {
        draft.pageId = page.id;
        draft.pages[0].isHomePage = false;
        // NOTE move new homePage to the top
        draft.pages.unshift(draft.pages.splice(originalPageIdx, 1)[0]);
      }
      draft.pages.sort(sortByNameWithHomepageOnTop);
    }),
  ),

  on(blazePageActions.pageCreationExecuted, (state, { pageId }) =>
    produce(state, (draft) => {
      delete draft.pendingPageCreations[pageId];
    }),
  ),

  /*** Runtime Action ***/

  on(
    runtimeActions.addRelatedElementWorkflowAndConnections,
    (state, { workflow, connectionFromWorkflow, connectionToWorkflow }) =>
      produce(state, (draft) => {
        draft.workflows.push(workflow);
        draft.connections.push(connectionFromWorkflow, connectionToWorkflow);
      }),
  ),

  on(runtimeActions.applyAiGeneratedWorkflow, (state, { workflow, elementId, inputConnections }) =>
    produce(state, (draft) => {
      applyAiGeneratedWorkflowMutably(draft, workflow, elementId, inputConnections);
    }),
  ),

  on(runtimeActions.applyWorkflow, (state, action) =>
    produce(state, (draft) => {
      const { workflow } = action;
      if (workflow?.id) {
        draft.workflowsStatus[workflow.id] = {
          phase: WorkflowPhase.Running,
        };
      }
    }),
  ),

  on(
    blazeActions.waitForPageAndStartRuntime,
    runtimeActions.clearDialogLikePageOutputs,
    (state, { pageId }) => {
      return produce(state, (draft) => {
        clearElementOutputsMutably(draft, pageId);
        for (const wf of state.workflows) {
          if (wf.pageId === pageId) {
            delete draft.workflowOutputs[wf.id];
          }
        }
      });
    },
  ),

  on(runtimeActions.clearOutputsAndSchema, (state, { accountSchemaChanged, userSchemaChanged }) =>
    produce(state, (draft) => {
      draft.connectionChains = {};
      draft.namespace = state.pageId;
      draft.pendingUi = {};
      draft.workflowOutputs = {};
      draft.workflowsStatus = {};

      const preventedKeys = [];
      for (const key of Object.keys(state.outputsUi)) {
        const element = state.entities[key.split('__')[0]];
        if (
          isVariantOfFormContainer(element) &&
          hasDataProps(element) &&
          isSelfBindingForm(element)
        ) {
          preventedKeys.push(key);
          preventedKeys.push(element.id);
          continue;
        }
        delete draft.outputsUi[key];
      }

      // [BL-6819] System table schemas (Accounts, Users) are cleared for reload only when their columns have changed.
      for (const key of Object.keys(state.schemas)) {
        if (
          (draft.schemas[key].name === 'Accounts' && accountSchemaChanged) ||
          (draft.schemas[key].name === 'Users' && userSchemaChanged)
        ) {
          delete draft.schemas[key];
        }
      }
    }),
  ),

  on(runtimeActions.connectionAdd, (state, { connection }) =>
    produce(state, (draft) => {
      if (Array.isArray(connection)) {
        draft.connections.push(...connection);
      } else {
        draft.connections.push(connection);
      }
    }),
  ),

  on(runtimeActions.connectionsAdd, (state, { ns, source, eventType, target }) =>
    produce(state, (draft) => {
      addConnectionMutably(draft, ns, source, eventType, target);
    }),
  ),

  on(runtimeActions.connectionsAddOrReplace, (state, { connections }) =>
    produce(state, (draft) => {
      for (const connection of connections) {
        addOrUpdateConnectionMutably(draft, connection);
      }
    }),
  ),

  on(runtimeActions.connectionRemove, (state, { id }) =>
    produce(state, (draft) => {
      draft.connections = draft.connections.filter((connection) => connection.id !== id);
    }),
  ),

  on(runtimeActions.connectionsReplace, (state, { ns, connections }) =>
    produce(state, (draft) => {
      replaceConnectionsForNsMutably(draft, ns, connections);
    }),
  ),

  on(runtimeActions.clearFormState, (state, { formElementId }) =>
    produce(state, (draft) => {
      delete draft.forms[formElementId];
    }),
  ),

  on(
    runtimeActions.connectionStepComplete,
    (state, { event, workflow, phase, phaseInfo, outputData, workflowSchema, updateSource }) =>
      produce(state, (draft) => {
        const chainId = event.connection.chainId;
        const completedId = event.connection.id;

        const ns = event.ns;
        const chains = state.connectionChains[ns] || [];

        const chainIndex = getChainIndexByChainId(chains, chainId);
        const chain = getChainByChainId(chains, chainId);
        const connectionIndex = chain?.findIndex((connection) => connection.id === completedId);

        if (updateSource === 'workflow') {
          // previously this part was handled by storeRuntimeElementOutput action
          const outputId = event.connection.target.id;
          const outputLayerType = event.connection.target.layerType;
          // Note for the future -> the 'Ui' case was removed from here,
          if (outputLayerType === 'Workflow') {
            draft.workflowOutputs[outputId] = outputData;
            if (workflowSchema && !!Object.keys(workflowSchema)?.length) {
              draft.schemas[outputId] = workflowSchema;
            }
          }
        } else if (
          !updateSource &&
          phase === WorkflowPhase.Blocked &&
          event.connection.source.layerType === 'Ui' &&
          state.entities[event.connection.source.id]?.type === 'button'
        ) {
          const formConnection = state.connections.find(
            (c) =>
              c.target.layerType === 'Workflow' &&
              c.target.id === workflow?.id &&
              c.source.layerType === 'Ui' &&
              isEditableForm(state.entities[c.source.id]),
          );
          const formState = formConnection ? draft.forms[formConnection.source.id] : undefined;
          if (formState) {
            // a form was technically valid but something blocked the wf
            // and the form `posted` state needs resetting
            formState.posted = false;
          }
        }

        // Clear pending Ui for workflow in ns
        if (workflow?.id) {
          const pendingInNs = Object.keys(draft.pendingUi).filter(
            (id) => getNamespaceId(id) === ns,
          );
          const cause = isActionWorkflow(event.connection, workflow.id) ? 'row-action' : 'workflow';
          removePendingUiMutably(draft, pendingInNs, { cause, workflowId: workflow.id });
        }

        if (!chain || !chain[connectionIndex]) {
          console.warn(`Page was changed from ${ns} to ${state.pageId} before workflow completed`);
          // This may happen if user changes page before the workflow fully completes. In such a case, we want to look
          // for the proper namespace and clear the queue if it's not the current namespace.
          chains.forEach((connectionChain) => {
            const staleConnection = connectionChain?.find((conn) => conn.id === completedId);
            if (staleConnection && staleConnection?.chainId) {
              draft.connectionChains[staleConnection?.chainId] = [];
            }
          });
        }

        if (chain[0]?.id === event.connection.id) {
          chain.shift();
          if (chain.length) {
            draft.connectionChains[ns][chainIndex] = chain;
          } else {
            draft.connectionChains[ns].splice(chainIndex, 1);
          }
        }

        // Clear workflow status
        if (workflow?.id && (!phase || phase === WorkflowPhase.Completed)) {
          draft.workflowsStatus[workflow.id] = {};
        } else if (workflow?.id && phase) {
          draft.workflowsStatus[workflow.id] = { phase, phaseInfo };
        }

        /**
         * Cleanup after pagination was executed.
         * Mostly direction to prevent page moving when other actions
         * like Delete, Update are executed on paginated element.
         */
        const targetId = event.connection.target?.id;
        if (targetId) {
          delete draft.pendingPaginations[targetId];
        }
      }),
  ),

  on(runtimeActions.executeUiWorkflow, (state, { elementId }) => {
    return {
      ...state,
      initializedUiElements: {
        ...state.initializedUiElements,
        [elementId]: false,
      },
    };
  }),

  on(
    runtimeActions.formSubmitActionUpdated,
    (state, { workflow, formConnection, buttonConnection }) =>
      produce(state, (draft) => {
        addOrUpdateWorkflowMutably(draft, workflow);
        addOrUpdateConnectionMutably(draft, formConnection);
        if (buttonConnection) {
          addOrUpdateConnectionMutably(draft, buttonConnection);
        }
      }),
  ),

  on(
    runtimeActions.formGroupSubmitActionUpdated,
    (state, { workflow, buttonConnection, formsStatusConnection, formsConnections }) =>
      produce(state, (draft) => {
        addOrUpdateWorkflowMutably(draft, workflow);
        if (buttonConnection) {
          addOrUpdateConnectionMutably(draft, buttonConnection);
        }
        if (formsStatusConnection) {
          addOrUpdateConnectionMutably(draft, formsStatusConnection);
        }
        formsConnections.forEach((formConnection) => {
          addOrUpdateConnectionMutably(draft, formConnection);
        });
      }),
  ),

  on(runtimeActions.initializeElementQuery, (state, { elementId, props }) =>
    produce(state, (draft) => {
      const elementQuery: ElementQueryState = {
        elementId,
        initialDataReceived: false,
        pageIndex: 0,
        pageSize: props.pageSize || DEFAULT_PAGE_SIZE,
        backwardPaginationSupported: true,
        query: props.query,
        source: 'init',
      };

      if (props.sortDirection && props.sortColumnName) {
        elementQuery.sort = {
          direction: props.sortDirection,
          columnName: props.sortColumnName,
        };
      }

      draft.elementsQuery[elementId] = elementQuery;
    }),
  ),

  on(runtimeActions.updateElementQueryNode, (state, { elementId, query }) => {
    const elementQueryState = deepClone(state.elementsQuery[elementId]);

    if (!elementQueryState) {
      return state;
    }

    elementQueryState.query = query;

    return {
      ...state,
      elementsQuery: {
        ...state.elementsQuery,
        [elementId]: elementQueryState,
      },
    };
  }),

  on(appStateActions.loadProject, (state, { currentPageId }) =>
    produce(state, (draft) => {
      draft.pageId = currentPageId;
    }),
  ),

  on(runtimeActions.markPendingUi, (state, { elementIds, changePending }) => {
    const pendingUi = { ...state.pendingUi };
    elementIds.forEach((id) => {
      if (!pendingUi[id]) {
        pendingUi[id] = [];
      }
      pendingUi[id] = [...pendingUi[id], changePending];
    });

    return {
      ...state,
      pendingUi,
    };
  }),

  on(runtimeActions.markPendingUiFinish, (state, { elementIds, changePending }) =>
    produce(state, (draft) => {
      removePendingUiMutably(draft, elementIds, changePending);
    }),
  ),

  on(runtimeActions.prefilledPageLayoutGenerated, (state, { workflow, connections }) =>
    produce(state, (draft) => {
      if (workflow) {
        addOrUpdateWorkflowMutably(draft, workflow);
      }
      if (connections && connections.length > 0) {
        connections.forEach((connection) => addOrUpdateConnectionMutably(draft, connection));
      }
    }),
  ),

  on(runtimeActions.queueConnections, (state, { ns, skipNamespaceChange }) => {
    return produce(state, (draft) => {
      // When connections get added to the specific namespace, it means that we should also start executing them in that
      // namespace.
      if (!skipNamespaceChange) {
        draft.namespace = ns;
      }
    });
  }),

  on(runtimeActions.queueConnectionChain, (state, { connectionChain, ns }) =>
    produce(state, (draft) => {
      const existingChains: ConnectionChain[] = [...(state.connectionChains[ns] || [])];

      // the check for existing connection in another chain,
      // caused events to be skipped (& sometimes pagination broke BL-6960).
      // so for the time being we'll queue all chains new
      const chains: ConnectionChain[] = [...existingChains, connectionChain];

      if (!draft.connectionChains[ns]) {
        draft.connectionChains[ns] = [];
      }

      draft.connectionChains[ns] = chains;
    }),
  ),

  on(runtimeActions.removeElementQuery, (state, { elementId }) =>
    produce(state, (draft) => {
      delete draft.elementsQuery[elementId];
    }),
  ),

  on(runtimeError, (state, { event, ns, workflowId }) =>
    produce(state, (draft) => {
      if (ns && workflowId) {
        draft.workflowOutputs[workflowId] = null;
        draft.workflowsStatus[workflowId] = {
          phase: WorkflowPhase.Error,
        };
      }
      if (ns) {
        const chainId = event?.connection?.chainId;
        const chainIndex = getChainIndexByChainId(state.connectionChains[ns], chainId);
        if (chainIndex >= 0) {
          draft.connectionChains[ns][chainIndex]?.shift();
        }
      } else {
        draft.connectionChains = {};
      }
      draft.pendingUi = {};
    }),
  ),

  on(runtimeActions.runtimeInfoFetched, (state, { projectId, schemas, workflows }) =>
    produce(state, (draft) => {
      draft.isRuntimeInfoFetched = true;
      draft.projectId = projectId;
      draft.schemas = schemas;
      draft.workflows = workflows;
    }),
  ),

  on(
    runtimeActions.runtimeUiEvent,
    (state, { elementId, data, output, schema, updateSource, eventType, eventContext }) => {
      if (schema && typeof schema === 'object') {
        if (isElementSchema(schema)) {
          // Store both for backwards compatibility
          state = {
            ...state,
            schemas: {
              ...state.schemas,
              [schema.id]: schema,
              [elementId]: schema,
            },
          };
        } else if (isWorkflowSchema(schema)) {
          const elementSchema = convertWorkflowSchemaToElementSchema(schema, elementId, eventType);
          state = {
            ...state,
            schemas: {
              ...state.schemas,
              [elementSchema.id]: elementSchema,
            },
          };
        }
      }

      switch (output) {
        case undefined: {
          const outputData = {
            updateSource,
            data,
            [BULK_ACTION_IDS_PROP]: eventContext?.[BULK_ACTION_IDS_PROP],
            userUpdate: eventContext?.userUpdate,
            isEmptyDataSelection: eventContext?.isEmptyDataSelection,
          };
          state = {
            ...state,
            outputsUi: {
              ...state.outputsUi,
              [createElementSchemaId(elementId, eventType)]: outputData,
            },
          };
          break;
        }
        case null:
          break;
        case 'events':
          state = {
            ...state,
            eventsUi: {
              ...state.eventsUi,
              [createElementSchemaId(elementId, eventType)]: {
                data,
                dataAction: eventContext?.dataAction,
              },
            },
          };
          break;
        case 'pagination':
          state = produce(state, (draft) => {
            updateQueryStateMutably(draft, data, elementId);
          });
          break;
        case 'data-filter': {
          const datasetId = 'datasetId' in data ? data.datasetId : undefined;
          if (!datasetId) {
            throw new Error('Missing datasetId in globalQuery');
          }
          state = {
            ...state,
            globalQuery: {
              ...state.globalQuery,
              [datasetId]: data,
            },
          };
          break;
        }
        default:
          console.warn(`Unknown output`, { elementId, output, data });
          break;
      }

      if (isActionEventType(eventType)) {
        const outputKey = createElementSchemaId(elementId, eventType);
        if (!state.outputsUi[outputKey]) {
          state = {
            ...state,
            outputsUi: {
              ...state.outputsUi,
              [outputKey]: { data: undefined },
            },
          };
        }
        state = {
          ...state,
          outputsUi: {
            ...state.outputsUi,
            [outputKey]: {
              ...(state.outputsUi[outputKey] as ElementOutputUi),
              [BULK_ACTION_IDS_PROP]: eventContext?.[BULK_ACTION_IDS_PROP],
            },
          },
        };
      }

      return state;
    },
  ),

  on(blazeActions.blazeTableUpdated, (state, { table, schema }) =>
    produce(state, (draft) => {
      draft.schemas[table.id] = schema;
    }),
  ),

  on(runtimeActions.schemasUpdate, runtimeActions.schemasUpdateForLayout, (state, { schemas }) =>
    produce(state, (draft) => {
      Object.entries(schemas).forEach(([id, dataset]) => {
        draft.schemas[id] = dataset;
      });
    }),
  ),

  on(runtimeActions.storeRuntimeElementSchema, (state, { element, schema, isPaginated }) => {
    if (isElementSchema(schema)) {
      // An element schema is keyed by its own id (`[elementId]__[eventType]`) only. Readers which
      // only know the element id resolve it through `getSchemaById`.
      state = {
        ...state,
        schemas: {
          ...state.schemas,
          [schema.id]: schema,
        },
      };
    } else if (schema && !!Object.keys(schema)?.length && element.layerType === 'Workflow') {
      state = {
        ...state,
        schemas: {
          ...state.schemas,
          [element.id]: schema,
        },
      };
    }
    if (element.layerType === 'Workflow' && element.id) {
      state = {
        ...state,
        workflowsPagination: {
          ...state.workflowsPagination,
          [element.id]: isPaginated,
        },
      };
    }

    return state;
  }),

  on(
    runtimeActions.storeElementPaginationTokens,
    (state, { elementId, paginationRequest, paginationResponse }) => {
      if (!elementId) {
        return state;
      }

      let elementPagination = state.elementsQuery[elementId];
      if (!elementPagination) {
        return state;
      }

      // Don't mutate the original object - shallow copy is enough here
      elementPagination = { ...elementPagination };
      elementPagination.source = 'workflow-execution';
      elementPagination.initialDataReceived = true;
      elementPagination.requireRefresh = false;
      let paginationFallbackSwitch = false;

      const backwardsSupport = paginationResponse?.backwardPaginationSupported !== false;
      const backwardsSupportChanged =
        elementPagination.backwardPaginationSupported !== backwardsSupport;
      elementPagination.backwardPaginationSupported = backwardsSupport;

      if (
        backwardsSupport &&
        elementPagination.previousPageToken &&
        !paginationResponse?.previousPageToken &&
        !paginationResponse?.nextPageToken
      ) {
        // no items on page
        elementPagination.currentPageToken = elementPagination?.previousPageToken;
        elementPagination.previousPageToken = elementPagination.previousPreviousPageToken;
        elementPagination.previousPreviousPageToken = null;
        elementPagination.nextPageToken = null;
        elementPagination.pageIndex = Math.max(0, elementPagination.pageIndex - 1);
        paginationFallbackSwitch = true;
        elementPagination.requireRefresh = elementPagination.pageIndex === 0;
      } else {
        elementPagination.currentPageToken = paginationRequest?.pageToken;
        if (elementPagination.previousPageToken) {
          elementPagination.previousPreviousPageToken = elementPagination.previousPageToken;
        }
        elementPagination.previousPageToken = paginationResponse?.previousPageToken;
        elementPagination.nextPageToken = paginationResponse?.nextPageToken;
      }

      // Total count comes only when trying to fetch first page
      if (elementPagination.pageIndex === 0 || paginationResponse?.totalCount !== null) {
        elementPagination.totalCount = paginationResponse?.totalCount;
      }

      // Check if a current page is in range
      let currentPageIndexOutOfBounds = false;
      if (!isNullish(paginationResponse?.totalCount)) {
        const totalPages = Math.ceil(paginationResponse.totalCount / elementPagination.pageSize);
        currentPageIndexOutOfBounds = totalPages <= elementPagination.pageIndex;
      }

      // Reset pagination when no previous page token is present.
      // When data source can be paginated only forwards ignore it.
      // In this case previous page token never exists.
      const shouldResetPagination =
        (!paginationFallbackSwitch &&
          ((!paginationResponse?.previousPageToken &&
            elementPagination.backwardPaginationSupported) ||
            backwardsSupportChanged)) ||
        currentPageIndexOutOfBounds;

      if (shouldResetPagination) {
        elementPagination.pageIndex = 0;
        elementPagination.totalCount = paginationResponse?.totalCount ?? 0;
        elementPagination.currentPageToken = '';
        elementPagination.previousPreviousPageToken = null;
      }

      return {
        ...state,
        elementsQuery: {
          ...state.elementsQuery,
          [elementId]: elementPagination,
        },
      };
    },
  ),

  on(runtimeActions.uiWorkflowExecuted, (state, { elementId }) => {
    return {
      ...state,
      initializedUiElements: {
        ...state.initializedUiElements,
        [elementId]: true,
      },
    };
  }),

  on(runtimeActions.updateFormContext, (state, { formElementId, formContext }) => {
    return {
      ...state,
      forms: {
        ...state.forms,
        [formElementId]: {
          ...state.forms[formElementId],
          context: formContext,
        },
      },
    };
  }),

  on(runtimeActions.updateFormState, (state, { formElementId, formState }) => {
    return {
      ...state,
      forms: {
        ...state.forms,
        [formElementId]: {
          ...state.forms[formElementId],
          ...formState,
        },
      },
    };
  }),

  on(runtimeActions.updateFormRowState, (state, { formElementId, rowState, rowId, formBlank }) =>
    produce(state, (draft) => {
      const formState = draft.forms[formElementId] as FormState<'editableList'>;
      if (!formState) {
        return;
      }

      if (rowState.valid) {
        delete formState.errors?.[rowId];
      } else {
        if (!formState.errors) {
          formState.errors = {};
        }
        formState.errors[rowId] = {
          valid: rowState.valid,
          errors: rowState.errors,
          displayIndex: rowState.displayIndex,
        };
      }

      formState.valid = Object.values(formState.errors ?? {}).every((row) => row?.valid ?? true);
      formState.blank = formBlank;
    }),
  ),

  on(runtimeActions.updateFormSubmitted, (state, { formElementId }) => {
    const formState = state.forms[formElementId];
    if (formState) {
      state = {
        ...state,
        forms: {
          ...state.forms,
          [formElementId]: {
            ...formState,
            submitted: true,
            posted: false,
            completed: false,
          },
        },
      };
    }

    return state;
  }),

  on(runtimeActions.updateFormPosted, (state, { formElementId }) => {
    const formState = state.forms[formElementId];
    if (formState) {
      state = {
        ...state,
        forms: {
          ...state.forms,
          [formElementId]: {
            ...formState,
            posted: true,
          },
        },
      };
    }

    return state;
  }),

  on(runtimeActions.updateFormReset, (state, { formElementId }) => {
    const formState = state.forms[formElementId];
    if (formState) {
      state = {
        ...state,
        forms: {
          ...state.forms,
          [formElementId]: {
            ...formState,
            submitted: false,
            posted: false,
            completed: false,
          },
        },
      };
    }

    return state;
  }),

  on(runtimeActions.updateFormCompleted, (state, { formElementId }) => {
    const formState = state.forms[formElementId];
    if (formState) {
      state = {
        ...state,
        forms: {
          ...state.forms,
          [formElementId]: {
            ...formState,
            submitted: false,
            posted: false,
            completed: true,
          },
        },
      };
    }

    return state;
  }),

  on(runtimeActions.updateRuntimeUiOutput, (state, { elementId, eventType, data, updateSource }) =>
    produce(state, (draft) => {
      const outputKey = createElementSchemaId(elementId, eventType);
      draft.outputsUi[outputKey] = { data, updateSource };
    }),
  ),

  on(runtimeActions.clearRuntimeUiOutputs, (state, { eventType, updateSource, elementIds }) =>
    produce(state, (draft) => {
      for (const elementId of elementIds) {
        const outputKey = createElementSchemaId(elementId, eventType);
        draft.outputsUi[outputKey] = { data: undefined, updateSource };
      }
    }),
  ),

  on(
    runtimeActions.updateWrapperConnections,
    (state, { elementId, inputWorkflow, connectionData }) =>
      produce(state, (draft) => {
        if (isDataFilter(state.entities[elementId])) {
          const gq = getGlobalQueryForElement(elementId, draft.globalQuery);
          if (gq?.datasetId) {
            delete draft.globalQuery[gq.datasetId];
            delete draft.outputsUi[createElementSchemaId(gq.elementId, 'change')];
          }
        }

        const dataFilterInputs = isDataFilter(state.entities[elementId])
          ? getDataFilterWorkflowsInputNames(state)
          : undefined;
        updateWrapperConnectionsMutably(
          draft,
          elementId,
          inputWorkflow,
          connectionData,
          dataFilterInputs,
        );

        // Reset element pagination inital data state after updating wrapper to avoid multiple workflow execution
        const elementPagination = draft.elementsQuery[elementId];
        if (elementPagination) {
          elementPagination.initialDataReceived = false;
        }
      }),
  ),

  on(runtimeActions.workflowsSet, (state, { workflows }) =>
    produce(state, (draft) => {
      draft.workflows = workflows;
    }),
  ),

  on(runtimeActions.workflowAdd, (state, { workflow }) =>
    produce(state, (draft) => {
      draft.workflows.push(workflow);
    }),
  ),

  on(runtimeActions.workflowsAdd, (state, { workflows }) =>
    produce(state, (draft) => {
      draft.workflows.push(...workflows);
    }),
  ),

  on(runtimeActions.workflowRemoveSuccess, (state, { workflowId }) =>
    produce(state, (draft) => {
      const workflow = state.workflows.find(({ id }) => id === workflowId);
      draft.workflows = state.workflows.filter(({ id }) => id !== workflowId);

      const connectionsIds = Object.values(workflow?.inputs || {});
      if (connectionsIds.length) {
        draft.connections = draft.connections.filter(
          (connection) => !connectionsIds.includes(connection.id),
        );
      }
    }),
  ),

  on(runtimeActions.workflowUpdate, (state, { workflow }) =>
    produce(state, (draft) => {
      updateWorkflowMutably(draft, workflow);
    }),
  ),

  on(runtimeActions.workflowsUpdate, (state, { workflows }) =>
    produce(state, (draft) => {
      for (const workflow of workflows) {
        updateWorkflowMutably(draft, workflow);
      }
    }),
  ),

  on(runtimeActions.upsertWorkflow, (state, { workflow }) =>
    produce(state, (draft) => {
      addOrUpdateWorkflowMutably(draft, workflow);
    }),
  ),

  on(runtimeActions.workflowsRemoved, (state, { workflowIds }) =>
    produce(state, (draft) => {
      draft.workflows = draft.workflows.filter(({ id }) => !workflowIds.includes(id));
    }),
  ),

  // Sanitize connection after possible visibility toggle (removal) of fields inside of a form or editableList
  on(elementsActions.formListRegenerated, (state, { removedElementIds }) =>
    produce(state, (draft) => {
      sanitizeConnectionsAndDataAfterUiChangeMutably(draft, removedElementIds);
    }),
  ),
  on(elementsActions.formRegenerated, (state, { elementIdsToRemove }) =>
    produce(state, (draft) => {
      sanitizeConnectionsAndDataAfterUiChangeMutably(draft, elementIdsToRemove);
    }),
  ),
  // The runtime half of a questionnaire commit: the wiring the regeneration orphaned goes
  // with the elements it removed, in the same action, so both slices move as one undo step.
  on(
    elementsActions.wizardQuestionnaireRegenerated,
    (state, { connectionIdsToRemove, elementIdsToRemove, wizardId }) =>
      produce(state, (draft) => {
        draft.connections = draft.connections.filter(
          ({ id }) => !connectionIdsToRemove.includes(id),
        );
        sanitizeConnectionsAndDataAfterUiChangeMutably(draft, elementIdsToRemove);
        dropStaleSlotSelectionMutably(draft, wizardId);
      }),
  ),

  on(elementsActions.layoutReady, (state, { layout }) =>
    produce(state, (draft) => {
      replaceConnectionsForNsMutably(draft, layout.pageId, layout.actionLayer.connections);
    }),
  ),

  on(elementsActions.swapElementTypeFormOutputUpdate, (state, { data, formOutputEventType }) =>
    produce(state, (draft) => {
      if (!data) {
        return;
      }
      if (!Array.isArray(data)) {
        data = [data];
      }

      for (const formUpdateData of data) {
        // restore linked field obj. when switching from an element that unwraps the rowId
        if (formUpdateData) {
          const {
            elementId,
            formId,
            formFieldName,
            valueFieldName,
            labelFieldName,
            previousOutputEventType,
            targetOutputEventType,
          } = formUpdateData;
          const formOutputKey = createElementSchemaId(formId, formOutputEventType);

          // Get form's state
          const stateOutputUi = state.outputsUi[formOutputKey];
          if (!stateOutputUi?.data) {
            return;
          }

          // Get original element's output
          const previousOutputKey = createElementSchemaId(elementId, previousOutputEventType);
          const elementOutput = state.outputsUi[previousOutputKey];

          // The previous element could have had another output event type => it has to be rewritten into the new output
          let inputValue = elementOutput?.data;

          if (!isDatasetLinkValue(inputValue)) {
            inputValue = {
              rowId: inputValue[valueFieldName],
              display: inputValue[labelFieldName],
            };
          }

          // Clear original output
          delete draft.outputsUi[previousOutputKey];
          // Populate the output data for the new default output's event type
          draft.outputsUi[createElementSchemaId(elementId, targetOutputEventType)] = elementOutput;

          // Doubled saving for legacy scenarios; previously, there was one output entry per element (without event type)
          // now the event type should be included so the data is not overwritten
          if (!draft.outputsUi[formOutputKey]) {
            draft.outputsUi[formOutputKey] = { data: {} };
          }
          (draft.outputsUi[formOutputKey] as ElementOutputUi).data[formFieldName] = inputValue;
        }
      }
    }),
  ),

  on(elementsActions.updateElementWrapperSuccess, (state, { workflow }) =>
    produce(state, (draft) => {
      const matchedWorkflow = draft.workflows.find(({ id }) => workflow.id === id);
      delete matchedWorkflow?.isDraft;
    }),
  ),

  on(
    elementsActions.updateDropdownDynamicSearchSuccess,
    (state, { userInputConnection, savedWorkflow, enable }) =>
      produce(state, (draft) => {
        if (enable) {
          if (!userInputConnection) {
            console.error('User input connection missing');
            return;
          }

          draft.connections = upsertConnections(state.connections, [userInputConnection]);
        } else if (userInputConnection) {
          draft.connections = draft.connections.filter(
            (connection) => connection.id !== userInputConnection.id,
          );
        }

        addOrUpdateWorkflowMutably(draft, savedWorkflow);
      }),
  ),

  on(runtimeActions.updateElementMeta, (state, { elementId, meta }) => {
    return {
      ...state,
      elementsMeta: {
        ...state.elementsMeta,
        [elementId]: deepmergeExceptArrays(
          state.elementsMeta[elementId] ?? {},
          meta,
        ) as ElementMetaData,
      },
    };
  }),

  // Update render element context
  on(runtimeActions.clearRenderElementContext, (state, { elementId, rowContext }) => {
    const key =
      elementId && rowContext?.rowId !== undefined
        ? `${elementId}__${rowContext.rowId}`
        : elementId;
    const renderContext = elementId ? state.elementsMeta[elementId]?.renderContext : undefined;

    if (elementId && key && renderContext) {
      state = {
        ...state,
        elementsMeta: {
          ...state.elementsMeta,
          [elementId]: {
            ...state.elementsMeta[elementId],
            renderContext: Object.fromEntries(
              Object.entries(renderContext).filter(([k]) => k !== key),
            ), // Remove given key from render context
          },
        },
      };
    }

    return state;
  }),
  on(runtimeActions.setRenderElementContext, (state, { elementId, context }) => {
    let renderContext = state.elementsMeta[elementId]?.renderContext;
    const key =
      elementId && context?.rowContext?.rowId !== undefined
        ? `${elementId}__${context.rowContext?.rowId}`
        : elementId;

    if (context) {
      renderContext = {
        ...renderContext,
        [key]: context,
      };
    } else if (renderContext) {
      renderContext = { ...renderContext };
      delete renderContext[key];
    }

    // Update element meta with new render context only if there's something to put there to avoid empty objects
    if (renderContext) {
      state = {
        ...state,
        elementsMeta: {
          ...state.elementsMeta,
          [elementId]: {
            ...state.elementsMeta[elementId],
            renderContext,
          },
        },
      };
    }

    return state;
  }),

  on(runtimeActions.updateElementMetaStepKill, (state, { elementId, slotId }) =>
    produce(state, (draft) => {
      const elementsMeta = draft.elementsMeta[elementId];
      const elementType = elementsMeta?.dataFeatures?.elementType;
      if (
        !elementsMeta ||
        (elementType !== 'wizard' && elementType !== 'tabs') ||
        elementsMeta.dataFeatures?.selectedSlotId !== slotId
      ) {
        return;
      }
      delete elementsMeta.dataFeatures;
    }),
  ),

  on(
    runtimeActions.workflowAiPromptCompleted,
    runtimeActions.containerActionAiPromptCompleted,
    (state, { workflow }) =>
      produce(state, (draft) => {
        addOrUpdateWorkflowMutably(draft, workflow);
      }),
  ),
];
